import React, { useMemo, useCallback, useReducer, useEffect, useState } from "react";
import { Button, Select, Badge } from "antd";
import Icon, { ArrowLeftOutlined, SyncOutlined, CloseOutlined, PercentageOutlined, NumberOutlined } from "src/icons";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import "./styles.scss";

import { useMetricsMetadata, useRedux } from "src/shared/hooks";
import { ReactComponent as FilterIcon } from "src/assets/filter-icon-horizontal.svg";
import MetricOperations, { formatDate } from "src/shared/models/MetricOperations";
import colors from "src/styles/variables.scss";
import { RouteComponentProps } from "react-router-dom";
import { StaticContext } from "react-router";
import { IRootState, trendlineData } from "src/shared/types";
import NSStatusHandler from "src/shared/components/NSStatusHandler";
import FiltersPane, { SelectedFilterValues } from "src/shared/components/FiltersPanev2";
import Center from "src/shared/components/Center";
import { sharedUtilsAbbreviateNumber, range } from "src/shared/utils";
import useDeepCompareEffect from "use-deep-compare-effect";
import ReactGA from "react-ga4";
import { TabNames, GRANULARITY_MAPPING } from "src/shared/config";
import { changeTimeTabAppWide } from "src/store/app";
import Tabs, { TabData } from "src/shared/components/Tabs";
import { CurrentDots, ActiveDots } from "src/shared/components/LineChart";
import appFetch from "src/shared/fetch";
import NS from "src/shared/utils/NS";
import { getTimeRoundOffData } from "src/shared/utils/timeUtils";
import styled from "@emotion/styled";

const SyncIcon = styled(SyncOutlined)`
  font-size: 1.5rem;
  height: min-content;
  width: 1.5rem;
  line-height: 15px;
`;

const { Option } = Select;

const chartcolors = [
  colors.chartdarkAxis,
  colors.downRed,
  colors.upGreen,
  "#AB47BC",
  "#6D4C41",
  "#00695C",
  colors.paleYellow,
  "#ff0000",
];

const MAX_SLOTS = 8;

function mapStateToProps(state: IRootState) {
  return {
    daterange: state.app.selectedTab,
    timerangeConfig: state.app.appConfig.payload,
    roles: state.app.userRoles.payload,
  };
}

type MetricData = { x: number; [x: string]: number };
function useMetricData(ids: { metricId: number; sliceId: number; filters: SelectedFilterValues }[]) {
  const [[res, status], setResponse] = useState<[{ data: any[]; lastUpdatedTimeStamp: number }, NS]>([
    { data: [], lastUpdatedTimeStamp: 0 },
    new NS("INIT"),
  ]);
  const { daterange, timerangeConfig, roles } = useRedux(mapStateToProps, {});
  const queryparams = new URLSearchParams([["timeTab", daterange]]).toString();

  const [url, body]: [string, any] = useMemo(() => {
    if (ids.length === 0) return ["", undefined];
    const urlpath = `/api/superset/render_filter_slices?${queryparams}`;
    const currentTimerangeConfig = MetricOperations.parseChronicFilter(timerangeConfig, daterange, "trendChart");
    const body = ids.map(({ metricId, sliceId, filters }) => {
      const restrictedFilters = !roles.hasFullAccess && !filters.city?.length ? { city: roles.cities } : filters;
      const desanitizedFilters = Object.entries(restrictedFilters).map(([k, v]) => ({ field: k, value: v }));
      return {
        filters: {
          adhoc_filters: desanitizedFilters,
          chronic_filters: currentTimerangeConfig,
        },
        metric_id: metricId,
        slice_id: sliceId,
        time_view: "trendChartId",
      };
    });
    return [urlpath, body.flat(1)];
  }, [ids, queryparams]);

  const getMetricData = () => {
    if (url) {
      setResponse([{ data: [], lastUpdatedTimeStamp: 0 }, new NS("LOADING")]);
      appFetch
        .post(url, { body, headers: { "Content-Type": "application/json" } })
        .then(({ response, error }) => {
          if (response) {
            setResponse([response, new NS("SUCCESS")]);
          } else if (error) {
            console.error(error);
            setResponse([{ data: [], lastUpdatedTimeStamp: 0 }, new NS("ERROR")]);
          }
        })
        .catch((err) => {
          console.error(err);
          setResponse([{ data: [], lastUpdatedTimeStamp: 0 }, new NS("ERROR")]);
        });
    }
  };

  useEffect(() => {
    getMetricData();
  }, [url, body]);

  if (!status.isSuccess) return [[] as MetricData[], "", 0, status, getMetricData] as const;
  const lastUpdatedOn = res?.lastUpdatedTimeStamp || 0;

  const parsedData = (res?.data || [])
    .filter((metric) => metric.time_view === "trendChartId")
    .map((entry, i) => {
      const parsed = JSON.parse(entry.data || null);
      if (parsed.error) status.status = "ERROR"; // druid issues
      if (!(parsed.data || [])[0]) status.status = "ERROR"; // need time values
      return {
        id: i,
        metricId: entry.metric_id,
        data: parsed.data[0].values as trendlineData,
      };
    });

  const granularity: string = GRANULARITY_MAPPING[daterange];

  const base = parsedData[0];
  const baseData = getTimeRoundOffData(base.data, granularity);

  const remaining = parsedData.slice(1);
  remaining.forEach((metric) => {
    metric.data = getTimeRoundOffData(metric.data, granularity);
  });

  type mergedType = MetricData & { [k: string]: number } & { map: { [k: number]: number }; formatted: string };
  const merged = baseData.reduce<mergedType[]>((acc, { x: baseX, y: baseY, formatted }, i) => {
    const timestamp = Number(baseX);
    const temp = {
      x: timestamp,
      map: { [base.id]: base.metricId as number },
      [base.id]: baseY,
      formatted,
    };
    remaining.forEach(({ data, id, metricId }) => {
      data.forEach((e) => {
        if (e.x === timestamp) {
          temp[id] = e.y;
          temp.map[id] = metricId;
        }
      });
      // If there is no entry corresponding to a time then we don't need to plot for that time
      if (!temp.map[id]) {
        temp[id] = undefined as any;
        temp.map[id] = metricId;
      }
    });
    // @ts-expect-error
    return acc.concat(temp);
  }, []);
  return [merged, granularity, lastUpdatedOn, status, getMetricData] as const;
}

function normalize2(input: MetricData[], keys: string[]) {
  const oldminmax = input.reduce<{ [k: string]: [number, number, number] }>((acc, value) => {
    keys.forEach((k) => {
      if (!acc[k]) acc[k] = [Number.MAX_VALUE, Number.MIN_VALUE, 0];
      if (value[k] < acc[k][0]) acc[k][0] = value[k];
      if (value[k] > acc[k][1]) acc[k][1] = value[k];
      acc[k][2] = value[k] / input.length;
    });
    return acc;
  }, {});

  return input.map((e) => {
    const r: MetricData = { x: e.x, map: e.map };
    keys.forEach((k) => {
      const [oldmin, oldmax, avg] = oldminmax[k];
      const oldrange = oldmax - oldmin;
      if (oldrange === 0) {
        r[k] = 0;
      } else {
        r[k] = (e[k] - avg) / oldrange;
      }
      r[`absolute-${k}`] = e[k];
    });
    return r;
  });
}

function DateRangeTabs() {
  const { daterange, setDateRange } = useRedux((s: IRootState) => ({ daterange: s.app.selectedTab }), {
    setDateRange: changeTimeTabAppWide,
  });

  const handleTabChange = useCallback((tabdata: TabData) => setDateRange(tabdata.key), []);

  return (
    <Tabs
      tabs={TabNames.map((tabName) => ({ title: tabName, key: tabName }))}
      page={daterange}
      onChange={handleTabChange}
    ></Tabs>
  );
}

type SelectedMetricAndFilters = { metricId: number; sliceId: number; filters: SelectedFilterValues };

type State = {
  selectedMetrics: SelectedMetricAndFilters[];
  unitType: "RELATIVE" | "ABSOLUTE";
  filterIndex: number | undefined;
  slots: number;
};
const initialState: State = {
  selectedMetrics: [],
  unitType: "ABSOLUTE",
  filterIndex: undefined,
  slots: 1,
};

function reducer(state: State, { type, payload }: { type: string; payload?: any }): State {
  switch (type) {
    case "UPDATE_SELECTED_METRICS": {
      const front = state.selectedMetrics.slice(0, payload.index);
      const current = {
        metricId: payload.metricId,
        filters: {},
        sliceId: payload.sliceId,
      };
      const back = state.selectedMetrics.slice(payload.index + 1);
      const sm: SelectedMetricAndFilters[] = [...front, current, ...back];
      return {
        ...state,
        selectedMetrics: sm,
        unitType: sm.length > 2 ? "RELATIVE" : state.unitType, // if selected <= 2 metrics, go with prev value
      };
    }
    case "SET_SELECTED_METRICS":
      return {
        ...state,
        selectedMetrics: payload,
        unitType: payload.length > 2 ? "RELATIVE" : state.unitType,
        slots: Math.max(1, payload.length),
      };
    case "SET_FILTER_INDEX":
      return { ...state, filterIndex: payload };
    case "SET_FILTERS":
      if (state.filterIndex === undefined) return state;
      return {
        ...state,
        selectedMetrics: [
          ...state.selectedMetrics.slice(0, state.filterIndex),
          { ...state.selectedMetrics[state.filterIndex], filters: payload },
          ...state.selectedMetrics.slice(state.filterIndex + 1),
        ],
        filterIndex: undefined,
      };
    case "TOGGLE_UNIT_TYPE":
      return { ...state, unitType: state.unitType === "RELATIVE" ? "ABSOLUTE" : "RELATIVE" };
    case "SET_SLOTS":
      return { ...state, slots: payload };
    case "REMOVE_SLOTS": {
      const shouldUpdateSelectedMetrics = !!state.selectedMetrics[payload];
      const sm: SelectedMetricAndFilters[] = shouldUpdateSelectedMetrics
        ? [...state.selectedMetrics.slice(0, payload), ...state.selectedMetrics.slice(payload + 1)]
        : state.selectedMetrics;
      return { ...state, slots: state.slots - 1, selectedMetrics: sm };
    }
    default:
      return state;
  }
}

function MetricComparison({
  history,
  location,
}: RouteComponentProps<{}, StaticContext, { compare?: SelectedMetricAndFilters[] }>) {
  const { daterange } = useRedux((s: IRootState) => ({ daterange: s.app.selectedTab }), {});
  const [{ selectedMetrics, slots, unitType, filterIndex }, dispatch] = useReducer(reducer, initialState);

  const [metrics, metricsStatus] = useMetricsMetadata();
  const [metricData, granularity, , dataStatus, refreshMetricData] = useMetricData(selectedMetrics);

  const data = unitType === "RELATIVE" ? normalize2(metricData, Object.keys(metricData[0]?.map || {})) : metricData;

  const handleMetricChange = (dropdownIndex: number) => (value: number) => {
    dispatch({
      type: "UPDATE_SELECTED_METRICS",
      payload: {
        index: dropdownIndex,
        sliceId: metrics.find((m) => m.metricId === value)?.trendChart || 0,
        metricId: value,
      },
    });
  };

  const handleFilterButtonClick = (index: number | undefined) => {
    dispatch({ type: "SET_FILTER_INDEX", payload: index });
  };

  const onFilterValuesChange = (values: SelectedFilterValues) => {
    dispatch({ type: "SET_FILTERS", payload: values });
  };

  const addSlot = () => dispatch({ type: "SET_SLOTS", payload: slots + 1 });
  const removeSlot = (index: number) => () => dispatch({ type: "REMOVE_SLOTS", payload: index });

  const goBack = useCallback(() => history.goBack(), []);

  const formatTooltip = (value: any, id: any, row) => {
    const payload = row.payload;
    const metricId = payload.map[id];
    const metric = metrics.find((m) => payload.map[id] === m.metricId);
    const absolute =
      unitType === "RELATIVE"
        ? sharedUtilsAbbreviateNumber(payload[`absolute-${id}`], 2)
        : sharedUtilsAbbreviateNumber(value, 2);
    return [absolute + (metric?.isPercentage ? "%" : ""), metrics.find((m) => m.metricId === metricId)?.label];
  };

  const getYAxisFormatter = (metricNo: number) => (v: number) => {
    const metric = metrics.find((m) => selectedMetrics[metricNo]?.metricId === m.metricId);
    const r = sharedUtilsAbbreviateNumber(v, 2);
    const suffix = (unitType === "ABSOLUTE" && metric?.isPercentage) || unitType === "RELATIVE" ? "%" : "";
    return r + suffix;
  };

  useEffect(() => {
    // https://stackoverflow.com/a/53611590
    if (location.state && location.state.compare) {
      const state = { ...location.state };
      const sm = state.compare;
      delete state.compare;
      history.replace("/metric-comparison", state);
      dispatch({ type: "SET_SELECTED_METRICS", payload: sm?.filter((m) => m !== undefined) });
    }
  }, []);

  const metricForFilters =
    filterIndex === undefined ? undefined : metrics.find((m) => m.metricId === selectedMetrics[filterIndex].metricId);
  const filterSliceId = metricForFilters?.filterSliceId || 0;
  const aggregationFields = metricForFilters?.aggregations || [];

  useDeepCompareEffect(() => {
    // `.sort` mutates object and we dont want that
    const sorted = [...selectedMetrics].sort((m1, m2) => m1.metricId - m2.metricId);
    if (selectedMetrics.length > 1) {
      (ReactGA as any).event({
        category: "Metric Comparison",
        label: sorted.map(({ metricId }) => metricId).join(","),
        action: daterange,
        dimension4: sorted
          .map(
            ({ metricId, filters }) =>
              `${metricId}:${Object.entries(filters)
                .map(([k, v]) => `${k}=${v.join(",")}`)
                .join("&")}`
          )
          .join(";"),
      });
    }
  }, [selectedMetrics]);

  const [shouldRefresh, setShouldRefresh] = useState(false);

  const handleRefreshClick = () => {
    if (selectedMetrics.length > 0 && !dataStatus.isLoading) {
      setShouldRefresh(true);
      refreshMetricData();
    }
  };

  useEffect(() => {
    if (!dataStatus.isLoading) {
      setShouldRefresh(false);
    }
  }, [dataStatus]);

  return (
    <section className="metric-comparison">
      <header>
        <Button icon={<ArrowLeftOutlined />} onClick={goBack} size="large" />
        <h1>Compare Metrics</h1>
        {dataStatus.isLoading || shouldRefresh ? <SyncIcon spin /> : <SyncIcon onClick={handleRefreshClick} />}
      </header>

      <DateRangeTabs />
      <NSStatusHandler status={metricsStatus}>
        {() => (
          <>
            <section className="chart-container">
              {selectedMetrics.length === 0 && (
                <Center style={{ color: "rgba(0,0,0,0.65)" }}>Select 2 or more metrics below to compare</Center>
              )}
              <NSStatusHandler status={dataStatus} style={{ color: "rgba(0,0,0,0.65)", fontSize: 14 }}>
                {() => (
                  <>
                    <div className="header">
                      <Button
                        type="primary"
                        icon={unitType === "RELATIVE" ? <NumberOutlined /> : <PercentageOutlined />}
                        size="small"
                        onClick={() => dispatch({ type: "TOGGLE_UNIT_TYPE" })}
                        disabled={selectedMetrics.length > 2}
                      >
                        {unitType === "RELATIVE" ? "Absolute" : "Relative"}
                      </Button>
                    </div>
                    <ResponsiveContainer height={260}>
                      <LineChart data={data} style={{ color: "inherit" }}>
                        <Tooltip
                          contentStyle={{ backgroundColor: "black", fontSize: 12 }}
                          formatter={formatTooltip}
                          wrapperStyle={{
                            opacity: 0.7,
                            maxWidth: "50vw",
                            overflow: "hidden",
                          }}
                        />
                        <XAxis dataKey="formatted" tickSize={2} height={16} tickMargin={8} />

                        <YAxis
                          axisLine={false}
                          yAxisId="left"
                          tickSize={0}
                          width={40}
                          tickCount={6}
                          tick={{
                            fill: unitType === "ABSOLUTE" ? chartcolors[0] : "inherit",
                            ...(unitType === "RELATIVE" && { visibility: "hidden" }),
                          }}
                          tickFormatter={getYAxisFormatter(0)}
                          tickMargin={4}
                        />

                        <YAxis
                          axisLine={false}
                          yAxisId="right"
                          tickSize={0}
                          width={32}
                          tickCount={6}
                          tickFormatter={getYAxisFormatter(Math.max(0, selectedMetrics.length - 1))}
                          tick={{
                            fill: chartcolors[Math.max(0, selectedMetrics.length - 1)],
                            ...(unitType === "RELATIVE" && { visibility: "hidden" }),
                          }}
                          orientation="right"
                          tickMargin={4}
                        />
                        {Object.keys(data[0]?.map || {}).map((id, i) => (
                          <Line
                            key={id}
                            type="linear"
                            dataKey={id}
                            strokeWidth={2}
                            stroke={chartcolors[id]}
                            connectNulls
                            yAxisId={i > 0 ? "right" : "left"}
                            dot={<CurrentDots color={chartcolors[id]} />}
                            activeDot={<ActiveDots color={chartcolors[id]} />}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
              </NSStatusHandler>
            </section>
            <div style={{ fontSize: 13, textAlign: "center", marginBottom: 32 }}>
              {selectedMetrics.length > 2 && "Comparision of 3 or more metrics is shown in relative scale"}
            </div>
            {range(slots).map((_, i) => (
              <div key={i} className="metric-select-container">
                <div className="label">
                  <span style={{ backgroundColor: chartcolors[i] }}></span>
                </div>
                <Select
                  onChange={handleMetricChange(i)}
                  placeholder="Select metric"
                  value={selectedMetrics[i]?.metricId}
                >
                  {metrics.map((m) => (
                    <Option key={m.metricId} value={m.metricId}>
                      {m.label}
                    </Option>
                  ))}
                </Select>
                <Badge count={Object.values(selectedMetrics[i]?.filters || {}).flat(1).length} dot>
                  <Button onClick={() => handleFilterButtonClick(i)} disabled={!selectedMetrics[i]}>
                    <Icon component={FilterIcon} />
                  </Button>
                </Badge>
                {i !== 0 && <Button onClick={removeSlot(i)} icon={<CloseOutlined className="text-lg" />} />}
              </div>
            ))}
            <Button onClick={addSlot} disabled={slots === MAX_SLOTS} ghost style={{ borderColor: "#d9d9d9" }}>
              + Add metric
            </Button>
          </>
        )}
      </NSStatusHandler>
      {filterIndex !== undefined && (
        <FiltersPane
          callerStatus={metricsStatus}
          sliceId={filterSliceId}
          initialValue={selectedMetrics[filterIndex].filters}
          onClose={() => handleFilterButtonClick(undefined)}
          aggregationFields={aggregationFields}
          onApply={onFilterValuesChange}
        />
      )}
    </section>
  );
}

export default React.memo(MetricComparison, () => true);
